import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Gallery from "./Gallery";
import { Card } from "primereact/card";
import { Helmet } from "react-helmet-async";
import "../CSS/RealEstatePage.css";
import axios from "axios";

interface RealEstate {
  id: string;
  title: string;
  description: string;
  price: number;
  location: string;
  propertyType: string;
  bedrooms: number;
  bathrooms: number;
  area: number;
  mainImage: string;
  galleryImages: string[];
  youtubeUrl: string;
  createdAt: number;
}

const RealEstatePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const url = `${window.location.origin}${location.pathname}`;
  const [property, setProperty] = useState<RealEstate | null>(null);
  const [loading, setLoading] = useState(true);

  const copyUrl = async () => {
    try {
      const shareUrl = `${process.env.REACT_APP_API}/real-estates/ssr/${id}`;
      if (!property) {
        await navigator.clipboard.writeText(shareUrl);
        return;
      }
      await navigator.clipboard.writeText(
        `${property.title} - ${property.propertyType} in ${property.location} - ${property.price.toLocaleString()} Read more at: ${shareUrl}`
      );
    } catch (err) {
      console.error("Failed to copy URL:", err);
    }
  };

  const getYouTubeVideoId = (url: string) => {
    if (!url) return null;

    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/,
      /youtube\.com\/watch\?.*v=([^&\s]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    return null;
  };

  useEffect(() => {
    const fetchProperty = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}/real-estates/${id}`
        );
        setProperty(response.data || null);
      } catch (error) {
        console.error("Error fetching property:", error);
        setProperty(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="property-page-loading">
        <div className="loading-spinner">Loading property details...</div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="property-page-error">
        <h2>Property not found</h2>
        <p>
          The property you're looking for doesn't exist or has been removed.
        </p>
      </div>
    );
  }

  const shareUrl = `${process.env.REACT_APP_API}/real-estates/ssr/${id}`;

  return (
    <div className="real-estate-page">
      <Helmet>
        <title>{property.title} | Real Estate</title>
        <meta name="description" content={property.description} />
        <meta property="og:title" content={property.title} />
        <meta property="og:description" content={property.description} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        {property.mainImage && (
          <>
            <meta property="og:image" content={property.mainImage} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
          </>
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={property.title} />
        <meta name="twitter:description" content={property.description} />
        {property.mainImage && (
          <meta name="twitter:image" content={property.mainImage} />
        )}
      </Helmet>

      <Card className="property-card">
        <div className="property-header">
          <h1 className="property-title">{property.title}</h1>
        </div>

        <div className="property-main-image">
          {property.mainImage ? (
            <img
              src={property.mainImage}
              alt={property.title}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = "/placeholder-image.jpg"; // You can add a placeholder image
                target.onerror = null; // Prevent infinite loop if placeholder also fails
              }}
            />
          ) : (
            <div className="no-image-placeholder">No image available</div>
          )}
        </div>

        <div className="social-share-buttons">
          <a
            className="social-btn whatsapp"
            rel="noreferrer noopener nofollow"
            href={`https://wa.me/?text=${encodeURIComponent(
              `${property.title} - ${property.propertyType} in ${property.location} - 
              ${property.price.toLocaleString()} Read more at: ${shareUrl}`
            )}`}
            target="_blank"
            title="Share on WhatsApp"
            aria-label="Share on WhatsApp"
          >
            <i className="pi pi-whatsapp"></i>
          </a>
          <a
            className="social-btn instagram"
            rel="noreferrer noopener nofollow"
            href={`https://www.instagram.com/sharer/sharer.php?u=${encodeURIComponent(
              `${property.title} - ${property.propertyType} in ${property.location} - ${property.price.toLocaleString()} Read more at: ${shareUrl}`
            )}`}
            target="_blank"
            title="Share on Instagram"
            aria-label="Share on Instagram"
          >
            <i className="pi pi-instagram"></i>
          </a>
          <a
            className="social-btn facebook"
            rel="noreferrer noopener nofollow"
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
              shareUrl
            )}`}
            target="_blank"
            title="Share on Facebook"
            aria-label="Share on Facebook"
          >
            <i className="pi pi-facebook"></i>
          </a>
          <button
            type="button"
            className="social-btn share-native"
            aria-label="Share"
            onClick={() => {
              const shareData = {
                url: shareUrl,
              };
              navigator.share(shareData).catch(console.error);
            }}
          >
            <i className="pi pi-share-alt"></i>
          </button>
          <button
            className="social-btn copy-link"
            onClick={copyUrl}
            title="Copy Link"
            aria-label="Copy Link"
          >
            <i className="pi pi-link"></i>
          </button>
        </div>

        <div className="property-details-grid">
          <div className="detail-item">
            <i className="pi pi-map-marker"></i>
            <span>{property.location}</span>
          </div>
          <div className="detail-item">
            <i className="pi pi-building"></i>
            <span>
              {property.propertyType.charAt(0).toUpperCase() +
                property.propertyType.slice(1)}
            </span>
          </div>
          {(property.propertyType === "house" ||
            property.propertyType === "apartment" ||
            property.propertyType === "condo" ||
            property.propertyType === "villa") && (
            <>
              <div className="detail-item">
                <i className="pi pi-bed"></i>
                <span>{property.bedrooms} Bedrooms</span>
              </div>
              <div className="detail-item">
                <i className="pi pi-bath"></i>
                <span>{property.bathrooms} Bathrooms</span>
              </div>
            </>
          )}
          <div className="detail-item">
            <i className="pi pi-ruler"></i>
            <span>{property.area} sqft</span>
          </div>
        </div>

        <div className="property-description">
          <h2>Description</h2>
          <p>{property.description}</p>
        </div>

        {property.galleryImages && property.galleryImages.length > 0 && (
          <div className="property-gallery">
            <h2>Gallery</h2>
            <Gallery images={property.galleryImages} isEditing={false} />
          </div>
        )}

        {property.youtubeUrl && (
          <div className="property-video-container mt-8">
            <h2 className="text-2xl font-bold mb-4">Video</h2>
            <div className="relative w-full h-[500px]">
              {getYouTubeVideoId(property.youtubeUrl) ? (
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeVideoId(
                    property.youtubeUrl
                  )}`}
                  title="Property Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <p className="text-gray-500">Invalid YouTube URL</p>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default RealEstatePage;
